import React, { useRef, useState } from "react";
import { Popover } from "@mantine/core";
import { useNotifications } from "../../context/NotificationContext";
import { useOutsidePress } from "../../hooks/useOutsidePress";
import { DESIGN_ICONS } from "../../theme/designIcons";
import { AlertsPanel, useAlertsRefresh } from "../notifications/AlertsPanel";
import alertStyles from "../notifications/Alerts.module.css";
import { RailButton } from "./RailButton";

interface AlertsRailButtonProps {
  onMouseEnter?: () => void;
}

/**
 * "Alerts" (formerly Notifications) in the rail's utility group: the unread
 * count, red while a fresh notification is waiting (isBellRed), and a click
 * opens the scrollable Alerts popover to the right of the rail; a press
 * anywhere else or Escape closes it.
 */
export const AlertsRailButton: React.FC<AlertsRailButtonProps> = ({
  onMouseEnter,
}) => {
  const { unacknowledgedCount, isBellRed } = useNotifications();
  const [opened, setOpened] = useState(false);
  useAlertsRefresh(opened);
  // Close on any press outside the button and panel. Capture-phase pointer
  // events (not Mantine's bubbling mousedown) so presses on the calendar grid,
  // whose drag-and-drop cancels pointer events, still count.
  const targetRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useOutsidePress(opened, () => setOpened(false), [targetRef, dropdownRef]);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="right-end"
      offset={10}
      width={440}
      closeOnClickOutside={false}
      classNames={{ dropdown: alertStyles.popover }}
    >
      <Popover.Target>
        <RailButton
          ref={targetRef}
          icon={DESIGN_ICONS.Bell}
          label="Alerts"
          badge={unacknowledgedCount}
          lit={opened}
          aria-haspopup="dialog"
          aria-expanded={opened}
          data-alert={isBellRed || undefined}
          onMouseEnter={onMouseEnter}
          onClick={() => setOpened((open) => !open)}
        />
      </Popover.Target>
      <Popover.Dropdown ref={dropdownRef} role="dialog" aria-label="Alerts">
        <AlertsPanel onNavigate={() => setOpened(false)} />
      </Popover.Dropdown>
    </Popover>
  );
};
